import React, { useEffect, useState } from "react";
import { Grid, Button, } from "@mui/material";
import { Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Area, AreaChart, } from "recharts";
import { getFailedTransactionReportAction } from "../../Actions/ReportsAction";
import {
    BadgeCheck,
    Banknote,
    Bot,
    CircleAlert,
    Compass,
    Wallet,
} from "lucide-react";

import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";
import ReportKpiCards from "./ReportKpiCards";
import {
    AreaGradient,
    ChartPanel,
    formatCompact,
    portalColor,
    rechartsAxis,
    rechartsGrid,
    ReportTooltip,
    STATUS_COLORS,
} from "./reportCharts";

const PORTAL_FILTER_OPTIONS = [
    { label: "All Portals", value: "all" },
    { label: "QBot", value: "qbot" },
    { label: "PayCentral", value: "paycentral" },
    { label: "KYC", value: "kyc" },
    { label: "Tripola", value: "tripola" },
];

const drawerFilters = [
    {
        name: "portal",
        label: "Portal",
        type: "select",
        defaultValue: "all",
        options: PORTAL_FILTER_OPTIONS,
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const FailedTransactionReport = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        portal: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [transactions, setTransactions] = useState([]);

    const buildApiFilters = (fv) => ({
        portal: fv.portal === "all" ? "" : fv.portal,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchReport = async (fv = filterValues) => {
        setLoading(true);
        try {
            const response = await getFailedTransactionReportAction(buildApiFilters(fv));
            const reportData = response?.data;
            setTransactions(reportData?.transactions || []);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { portal: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchReport(resetValues);
    };

    const hasAppliedFilters = filterValues.portal !== "all";

    const totalFailures = transactions.length;

    const failedAmount = transactions.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
    );

    const portalCount = {};
    const portalAmount = {};
    const dailyFailures = {};

    transactions.forEach((item) => {
        const portal = item.portal || "Unknown";
        portalCount[portal] = (portalCount[portal] || 0) + 1;
        portalAmount[portal] = (portalAmount[portal] || 0) + Number(item.amount || 0);
        const date = new Date(item.transactionDate).toLocaleDateString();
        dailyFailures[date] = (dailyFailures[date] || 0) + 1;
    });

    const portalChartData = Object.keys(portalCount).map((key) => ({
        name: key,
        value: portalCount[key],
    }));

    const portalAmountChartData = Object.keys(portalAmount).map((key) => ({
        name: key,
        value: Number(portalAmount[key].toFixed(2)),
    }));

    const dailyFailureChartData = Object.keys(dailyFailures).map((key) => ({
        date: key,
        count: dailyFailures[key],
    }));

    const qbotFailures = portalCount["QBot"] || 0;
    const paycentralFailures = portalCount["PayCentral"] || 0;
    const kycFailures = portalCount["KYC"] || 0;
    const tripolaFailures = portalCount["Tripola"] || 0;

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">
                                        Failed Transaction Report
                                    </h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                {/* KPI Cards */}
                <Grid item xs={12}>
                    <ReportKpiCards
                        loading={loading}
                        cards={[
                            {
                                key: "total",
                                label: "Total Failures",
                                value: totalFailures,
                                accent: "#dc2626",
                                icon: <CircleAlert />,
                            },
                            {
                                key: "amount",
                                label: "Failed Amount",
                                value: Number(failedAmount || 0),
                                accent: "#ea580c",
                                icon: <Banknote />,
                            },
                            {
                                key: "qbot",
                                label: "QBot Failures",
                                value: qbotFailures,
                                accent: "#22419e",
                                icon: <Bot />,
                            },
                            {
                                key: "paycentral",
                                label: "PayCentral Failures",
                                value: paycentralFailures,
                                accent: "#059669",
                                icon: <Wallet />,
                            },
                            {
                                key: "kyc",
                                label: "KYC Failures",
                                value: kycFailures,
                                accent: "#7c3aed",
                                icon: <BadgeCheck />,
                            },
                            {
                                key: "tripola",
                                label: "Tripola Failures",
                                value: tripolaFailures,
                                accent: "#0891b2",
                                icon: <Compass />,
                            },
                        ]}
                    />
                </Grid>

                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0, }}>
                    <Grid item xs={12} md={4}>
                        <ChartPanel
                            title="Failures by portal"
                            caption="Declined transaction count"
                            height={320}
                            loading={loading}
                            isEmpty={portalChartData.length === 0}
                        >
                            <ResponsiveContainer>
                                <BarChart data={portalChartData} barCategoryGap="34%">
                                    <CartesianGrid {...rechartsGrid} />
                                    <XAxis dataKey="name" {...rechartsAxis} />
                                    <YAxis
                                        {...rechartsAxis}
                                        width={40}
                                        tickFormatter={formatCompact}
                                    />
                                    <Tooltip
                                        content={<ReportTooltip />}
                                        cursor={{ fill: "rgba(190, 18, 60, 0.05)" }}
                                    />
                                    <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={38}>
                                        {portalChartData.map((entry) => (
                                            <Cell key={entry.name} fill={portalColor(entry.name)} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <ChartPanel
                            title="Failed amount by portal"
                            caption="Value lost to declined payments"
                            height={320}
                            loading={loading}
                            isEmpty={portalAmountChartData.length === 0}
                        >
                            <ResponsiveContainer>
                                <BarChart
                                    data={portalAmountChartData}
                                    layout="vertical"
                                    barCategoryGap="34%"
                                >
                                    <CartesianGrid
                                        stroke={rechartsGrid.stroke}
                                        strokeDasharray={rechartsGrid.strokeDasharray}
                                        horizontal={false}
                                    />
                                    <XAxis
                                        type="number"
                                        {...rechartsAxis}
                                        tickFormatter={formatCompact}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        {...rechartsAxis}
                                        width={78}
                                    />
                                    <Tooltip
                                        content={<ReportTooltip />}
                                        cursor={{ fill: "rgba(190, 18, 60, 0.05)" }}
                                    />
                                    <Bar dataKey="value" radius={[0, 8, 8, 0]} maxBarSize={26}>
                                        {portalAmountChartData.map((entry) => (
                                            <Cell key={entry.name} fill={portalColor(entry.name)} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <ChartPanel
                            title="Daily failure trend"
                            caption="Failures recorded per day"
                            height={320}
                            loading={loading}
                            isEmpty={dailyFailureChartData.length === 0}
                        >
                            <ResponsiveContainer>
                                <AreaChart data={dailyFailureChartData}>
                                    <AreaGradient
                                        id="failedTrendFill"
                                        color={STATUS_COLORS.failed}
                                    />
                                    <CartesianGrid {...rechartsGrid} />
                                    <XAxis dataKey="date" {...rechartsAxis} />
                                    <YAxis
                                        {...rechartsAxis}
                                        width={34}
                                        tickFormatter={formatCompact}
                                    />
                                    <Tooltip content={<ReportTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="count"
                                        stroke={STATUS_COLORS.failed}
                                        strokeWidth={2}
                                        fill="url(#failedTrendFill)"
                                        activeDot={{
                                            r: 4,
                                            fill: "#ffffff",
                                            stroke: STATUS_COLORS.failed,
                                            strokeWidth: 2,
                                        }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartPanel>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default FailedTransactionReport;
